import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// Create the context
const AppContext = createContext();

/**
 * Returns a minimal subset of the dietist object safe to persist in
 * localStorage. Only fields the UI actually uses are included so that a
 * potential XSS attack cannot exfiltrate sensitive profile data (email,
 * specializations, settings, etc.) from local storage.
 */
const pickDietistFields = (d) => ({
    _id:     d._id,
    name:    d.name,
    surname: d.surname,
});

// Create a provider component
export const AppProvider = ({ children }) => {
    const { i18n } = useTranslation();

    const [dietist, setDietist] = useState(() => {
        // Retrieve only the minimal cached fields from localStorage.
        // We intentionally do NOT store the full dietist object to limit
        // exposure in case of an XSS attack.
        try {
            const stored = localStorage.getItem('dietist');
            if (!stored) return null;
            const parsed = JSON.parse(stored);
            // Shape-check: must be an object with a valid-looking _id string
            // and name/surname that are short plain strings (guards against
            // stored payloads being used as injection vectors in the UI).
            if (
                parsed &&
                typeof parsed === 'object' &&
                typeof parsed._id === 'string' &&
                parsed._id.length === 24 &&
                typeof parsed.name === 'string' &&
                parsed.name.length <= 100 &&
                typeof parsed.surname === 'string' &&
                parsed.surname.length <= 100
            ) {
                return parsed;
            }
            // Discard malformed data
            localStorage.removeItem('dietist');
            return null;
        } catch {
            localStorage.removeItem('dietist');
            return null;
        }
    });

    const [currentLanguage, setCurrentLanguage] = useState(i18n.language)
    const [toursEnabled, setToursEnabled] = useState(true)

    // Fetch user preferences from the backend whenever the authenticated
    // dietist changes. The backend response takes precedence over local
    // defaults; missing fields fall back to safe values ("it" language,
    // tours enabled).
    useEffect(() => {
        if (!dietist?._id) return;
        let cancelled = false;
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/preferences`)
            .then((res) => {
                if (cancelled) return;
                const prefs = res.data ?? {};
                const lang = prefs.language ?? 'it';
                const tours = prefs.toursEnabled ?? true;
                i18n.changeLanguage(lang);
                setCurrentLanguage(lang);
                setToursEnabled(tours);
            })
            .catch(() => {
                // Non-blocking: silently keep the current defaults on failure
            });
        return () => { cancelled = true; };
    }, [dietist?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    const appColors = {
        primary: "#fd4701",
        primaryHover: "#830102",
        secondary: "#fc9f5b",
        tertiary: "#fbd1a2",
        background: "#fafcfb",
    }

    // Custom setter that strips the dietist object down to only the fields
    // the UI actually needs before persisting to localStorage.
    const setDietistSafe = (value) => {
        if (typeof value === 'function') {
            // Support functional updates: setDietist(prev => ...)
            setDietist(prev => {
                const next = value(prev);
                return next ? pickDietistFields(next) : next;
            });
        } else {
            setDietist(value ? pickDietistFields(value) : value);
        }
    };

    useEffect(() => {
        // Persist only the minimum required fields to localStorage
        if (dietist) {
            localStorage.setItem('dietist', JSON.stringify(pickDietistFields(dietist)));
        } else {
            localStorage.removeItem('dietist');
        }
    }, [dietist]);

    return (
        <AppContext.Provider value={{ dietist, setDietist: setDietistSafe, appColors, currentLanguage, setCurrentLanguage, toursEnabled, setToursEnabled }}>
            {children}
        </AppContext.Provider>
    );
};

// Custom hook to use the AppContext
export const useAppContext = () => {
    return useContext(AppContext);
};

export default AppContext;
